import { z } from 'zod';
import { baseMessageSchema } from './_base';

export enum PeerMessageType {
  PEER_GET_ID = 'PEER_GET_ID',
  PEER_GET_ID_ANSWER = 'PEER_GET_ID_ANSWER',
  PEER_SDP_OFFER = 'PEER_SDP_OFFER',
  PEER_SDP_ANSWER = 'PEER_SDP_ANSWER',
  PEER_ICE_CANDIDATE = 'PEER_ICE_CANDIDATE',
  PEER_ICE_COMPLETE = 'PEER_ICE_COMPLETE',
  PEER_STATS = 'PEER_STATS',
  PEER_STATS_SUCCESS = 'PEER_STATS_SUCCESS',
  PEER_ERROR = 'PEER_ERROR',
}

export enum PeerIdType {
  REMOTE_HOST = 'remoteHost',
  REMOTE_CLIENT = 'remoteClient',
  HOST = 'host',
  PLAYER = 'player',
}

export type SdpPayload = z.infer<typeof sdpSchema>;
export type IceCandidatePayload = z.infer<typeof iceCandidateSchema>;

// Common schemas for WebRTC data
export const sdpSchema = z.object({
  type: z.enum(['offer', 'answer', 'pranswer', 'rollback']),
  sdp: z.string(),
});

// from RTCIceCandidate
const iceCandidateSchema = z.object({
  json: z.array(z.string()),
});

// Stats schema (can be expanded based on what stats you want to collect)
const rtcStatsSchema = z.object({
  timestamp: z.number(),
  type: z.string(),
  bitrate: z.number().optional(),
  packetLoss: z.number().optional(),
  roundTripTime: z.number().optional(),
  // Add other stats as needed
});

// Payload schemas
const peerGetIdPayloadSchema = z.object({
  peerIdType: z.nativeEnum(PeerIdType),
  peerId: z.string().optional(),
  peerToken: z.string().optional(),
});

const peerGetIdAnswerPayloadSchema = z.object({
  peerId: z.string(),
  peerIdType: z.nativeEnum(PeerIdType),
  peerToken: z.string().optional(),
  iceServers: z
    .object({
      urls: z.array(z.string()),
      username: z.string(),
      credential: z.string(),
    })
    .optional(),
});

const peerSdpOfferPayloadSchema = z.object({
  fromId: z.string(),
  toId: z.string(),
  sdp: sdpSchema,
});

const peerSdpAnswerPayloadSchema = z.object({
  fromId: z.string(),
  toId: z.string(),
  sdp: sdpSchema,
});

const peerIceCandidatePayloadSchema = z.object({
  fromId: z.string(),
  toId: z.string(),
  candidates: iceCandidateSchema,
});

const peerIceCompletePayloadSchema = z.object({
  fromId: z.string(),
  toId: z.string(),
});

const peerStatsPayloadSchema = z.object({
  peerId: z.string(),
  stats: rtcStatsSchema,
});

const peerStatsSuccessPayloadSchema = z.object({
  acknowledged: z.boolean(),
});

const peerErrorPayloadSchema = z.object({
  peerId: z.string(),
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

// Full message schemas
const peerGetIdSchema = baseMessageSchema
  .extend({
    type: z.literal(PeerMessageType.PEER_GET_ID),
  })
  .merge(peerGetIdPayloadSchema);

const peerGetIdAnswerSchema = baseMessageSchema
  .extend({
    type: z.literal(PeerMessageType.PEER_GET_ID_ANSWER),
  })
  .merge(peerGetIdAnswerPayloadSchema);

const peerSdpOfferSchema = baseMessageSchema
  .extend({
    type: z.literal(PeerMessageType.PEER_SDP_OFFER),
  })
  .merge(peerSdpOfferPayloadSchema);

const peerSdpAnswerSchema = baseMessageSchema
  .extend({
    type: z.literal(PeerMessageType.PEER_SDP_ANSWER),
  })
  .merge(peerSdpAnswerPayloadSchema);

const peerIceCandidateSchema = baseMessageSchema
  .extend({
    type: z.literal(PeerMessageType.PEER_ICE_CANDIDATE),
  })
  .merge(peerIceCandidatePayloadSchema);

const peerIceCompleteSchema = baseMessageSchema
  .extend({
    type: z.literal(PeerMessageType.PEER_ICE_COMPLETE),
  })
  .merge(peerIceCompletePayloadSchema);

const peerStatsSchema = baseMessageSchema
  .extend({
    type: z.literal(PeerMessageType.PEER_STATS),
  })
  .merge(peerStatsPayloadSchema);

const peerStatsSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(PeerMessageType.PEER_STATS_SUCCESS),
  })
  .merge(peerStatsSuccessPayloadSchema);

const peerErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(PeerMessageType.PEER_ERROR),
  })
  .merge(peerErrorPayloadSchema);

export const peerMessageSchema = z.discriminatedUnion('type', [
  peerGetIdSchema,
  peerGetIdAnswerSchema,
  peerSdpOfferSchema,
  peerSdpAnswerSchema,
  peerIceCandidateSchema,
  peerIceCompleteSchema,
  peerStatsSchema,
  peerStatsSuccessSchema,
  peerErrorSchema,
]);

// Export payload types
export type PeerGetIdPayload = z.infer<typeof peerGetIdPayloadSchema>;
export type PeerGetIdAnswerPayload = z.infer<typeof peerGetIdAnswerPayloadSchema>;
export type PeerSdpOfferPayload = z.infer<typeof peerSdpOfferPayloadSchema>;
export type PeerSdpAnswerPayload = z.infer<typeof peerSdpAnswerPayloadSchema>;
export type PeerIceCandidatePayload = z.infer<typeof peerIceCandidatePayloadSchema>;
export type PeerIceCompletePayload = z.infer<typeof peerIceCompletePayloadSchema>;
export type PeerStatsPayload = z.infer<typeof peerStatsPayloadSchema>;
export type PeerStatsSuccessPayload = z.infer<typeof peerStatsSuccessPayloadSchema>;
export type PeerErrorPayload = z.infer<typeof peerErrorPayloadSchema>;

// Full message type
export type PeerMessage = z.infer<typeof peerMessageSchema>;

type PayloadType<T extends PeerMessageType> = T extends PeerMessageType.PEER_GET_ID
  ? PeerGetIdPayload
  : T extends PeerMessageType.PEER_GET_ID_ANSWER
    ? PeerGetIdAnswerPayload
    : T extends PeerMessageType.PEER_SDP_OFFER
      ? PeerSdpOfferPayload
      : T extends PeerMessageType.PEER_SDP_ANSWER
        ? PeerSdpAnswerPayload
        : T extends PeerMessageType.PEER_ICE_CANDIDATE
          ? PeerIceCandidatePayload
          : T extends PeerMessageType.PEER_ICE_COMPLETE
            ? PeerIceCompletePayload
            : T extends PeerMessageType.PEER_STATS
              ? PeerStatsPayload
              : T extends PeerMessageType.PEER_STATS_SUCCESS
                ? PeerStatsSuccessPayload
                : T extends PeerMessageType.PEER_ERROR
                  ? PeerErrorPayload
                  : never;

export function generatePeerMessage<T extends PeerMessageType>(type: T, payload: PayloadType<T>): PeerMessage {
  const base = {
    id: crypto.randomUUID(),
    timestamp: Date.now(),
    type,
  };

  switch (type) {
    case PeerMessageType.PEER_GET_ID:
      return peerGetIdSchema.parse({
        ...base,
        ...payload,
      });
    case PeerMessageType.PEER_GET_ID_ANSWER:
      return peerGetIdAnswerSchema.parse({
        ...base,
        ...payload,
      });
    case PeerMessageType.PEER_SDP_OFFER:
      return peerSdpOfferSchema.parse({
        ...base,
        ...payload,
      });
    case PeerMessageType.PEER_SDP_ANSWER:
      return peerSdpAnswerSchema.parse({
        ...base,
        ...payload,
      });
    case PeerMessageType.PEER_ICE_CANDIDATE:
      return peerIceCandidateSchema.parse({
        ...base,
        ...payload,
      });
    case PeerMessageType.PEER_ICE_COMPLETE:
      return peerIceCompleteSchema.parse({
        ...base,
        ...payload,
      });
    case PeerMessageType.PEER_STATS:
      return peerStatsSchema.parse({
        ...base,
        ...payload,
      });
    case PeerMessageType.PEER_STATS_SUCCESS:
      return peerStatsSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case PeerMessageType.PEER_ERROR:
      return peerErrorSchema.parse({
        ...base,
        ...payload,
      });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled message type: ${exhaustiveCheck}`);
    }
  }
}
